import React, { useState } from 'react';
import styled from 'styled-components';
import {
  PageLayout,
  MainContent,
  ContentContainer,
  PageTitle,
} from '../../styles/SharedStyles';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import {
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  Alert,
  Checkbox,
  FormGroup,
  FormLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';

type RoleType = 'admin' | 'travel_expert' | 'billing';

interface Role {
  id: RoleType;
  label: string;
  icon: JSX.Element;
  description: string;
  color: string;
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  roles: RoleType[];
  status: 'active' | 'pending' | 'inactive';
  profileUrl: string;
}

const ROLES: Role[] = [
  {
    id: 'admin',
    label: 'Admin',
    icon: <AdminPanelSettingsIcon />,
    description: 'Full access to all settings and user management',
    color: '#805AD5'
  },
  {
    id: 'travel_expert',
    label: 'Travel Expert',
    icon: <FlightTakeoffIcon />,
    description: 'Can manage leads and create travel itineraries',
    color: '#3182CE'
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: <AttachMoneyIcon />,
    description: 'Access to billing and payment settings',
    color: '#38A169'
  }
];

const TeamSettingsPage: React.FC = () => {
  const [members, setMembers] = useState<TeamMember[]>([
    {
      id: '1',
      name: 'Jane Smith',
      email: 'jane@wanderlustadventures.com',
      phone: '+1 (555) 123-4567',
      roles: ['admin', 'travel_expert'],
      status: 'active',
      profileUrl: '/path/to/jane.jpg'
    },
    {
      id: '2',
      name: 'John Doe',
      email: 'john@wanderlustadventures.com',
      phone: '+1 (555) 234-5678',
      roles: ['travel_expert'],
      status: 'active',
      profileUrl: '/path/to/john.jpg'
    },
    {
      id: '3',
      name: 'Alice Johnson',
      email: 'alice@wanderlustadventures.com',
      phone: '+1 (555) 345-6789',
      roles: ['billing', 'travel_expert'],
      status: 'active',
      profileUrl: '/path/to/alice.jpg'
    }
  ]);

  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isEditingMember, setIsEditingMember] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [newMember, setNewMember] = useState<Partial<TeamMember>>({
    name: '',
    email: '',
    phone: '',
    roles: [],
    status: 'active'
  });

  const handleAddMember = () => {
    if (newMember.name && newMember.email && newMember.phone && newMember.roles) {
      const member: TeamMember = {
        id: Date.now().toString(),
        name: newMember.name,
        email: newMember.email,
        phone: newMember.phone,
        roles: newMember.roles,
        status: 'active',
        profileUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(newMember.name)}&background=random`
      };
      setMembers([...members, member]);
      setIsAddingMember(false);
      setNewMember({ name: '', email: '', phone: '', roles: [], status: 'active' });
    }
  };

  const handleEditMember = (member: TeamMember) => {
    setSelectedMember(member);
    setIsEditingMember(true);
  };

  const handleUpdateMember = () => {
    if (selectedMember) {
      setMembers(members.map(m =>
        m.id === selectedMember.id ? selectedMember : m
      ));
      setIsEditingMember(false);
      setSelectedMember(null);
    }
  };

  const handleRemoveMember = (id: string) => {
    setMembers(members.filter(member => member.id !== id));
  };

  const handleRoleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    role: RoleType,
    isEditing: boolean
  ) => {
    const target = isEditing ? selectedMember : newMember;
    const setTarget = isEditing ? setSelectedMember : setNewMember;

    if (!target) return;

    const currentRoles = target.roles || [];
    const newRoles = event.target.checked
      ? [...currentRoles, role]
      : currentRoles.filter(r => r !== role);

    if (isEditing && selectedMember) {
      setSelectedMember({
        ...selectedMember,
        roles: newRoles
      });
    } else {
      setNewMember({
        ...newMember,
        roles: newRoles
      });
    }
  };

  const renderRoleChips = (memberRoles: RoleType[]) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {memberRoles.map(role => {
        const roleConfig = ROLES.find(r => r.id === role)!;
        return (
          <Tooltip key={role} title={roleConfig.description}>
            <Chip
              icon={roleConfig.icon as any}
              label={roleConfig.label}
              size="small"
              sx={{
                backgroundColor: roleConfig.color,
                color: 'white',
                '& .MuiSvgIcon-root': {
                  color: 'white'
                }
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );

  const renderRoleSelection = (
    currentRoles: RoleType[] = [],
    isEditing: boolean = false
  ) => (
    <FormControl component="fieldset" sx={{ mt: 2 }}>
      <FormLabel component="legend">Roles</FormLabel>
      <FormGroup>
        {ROLES.map(role => (
          <FormControlLabel
            key={role.id}
            control={
              <Checkbox
                checked={currentRoles.includes(role.id)}
                onChange={(e) => handleRoleChange(e, role.id, isEditing)}
                icon={role.icon}
                checkedIcon={role.icon}
                sx={{
                  '&.Mui-checked': {
                    color: role.color
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body1">{role.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {role.description}
                </Typography>
              </Box>
            }
          />
        ))}
      </FormGroup>
    </FormControl>
  );

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <PageTitle variant="h4" fontWeight={600} gutterBottom>Team Settings</PageTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddingMember(true)}
            >
              Add Team Member
            </Button>
          </Box>

          <List>
            {members.map(member => (
              <ListItem
                key={member.id}
                sx={{
                  bgcolor: 'background.paper',
                  mb: 2,
                  borderRadius: 1,
                  boxShadow: 1
                }}
              >
                <ListItemAvatar>
                  <Avatar src={member.profileUrl} alt={member.name}>
                    {member.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">{member.name}</Typography>
                      <Chip
                        label={member.status}
                        size="small"
                        color={member.status === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Typography color="text.secondary">{member.email}</Typography>
                        <Typography color="text.secondary">{member.phone}</Typography>
                      </Box>
                      {renderRoleChips(member.roles)}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => handleEditMember(member)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveMember(member.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {/* Add Member Dialog */}
          <Dialog
            open={isAddingMember}
            onClose={() => setIsAddingMember(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                fullWidth
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Phone"
                type="tel"
                fullWidth
                value={newMember.phone}
                onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
              {renderRoleSelection(newMember.roles, false)}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsAddingMember(false)}>Cancel</Button>
              <Button
                onClick={handleAddMember}
                variant="contained"
                disabled={!newMember.name || !newMember.email || !newMember.phone || !newMember.roles?.length}
              >
                Add
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Member Dialog */}
          <Dialog
            open={isEditingMember}
            onClose={() => setIsEditingMember(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                fullWidth
                value={selectedMember?.name}
                onChange={(e) => selectedMember && setSelectedMember({
                  ...selectedMember,
                  name: e.target.value
                })}
              />
              <TextField
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                value={selectedMember?.email}
                onChange={(e) => selectedMember && setSelectedMember({
                  ...selectedMember,
                  email: e.target.value
                })}
              />
              <TextField
                margin="dense"
                label="Phone"
                type="tel"
                fullWidth
                value={selectedMember?.phone}
                onChange={(e) => selectedMember && setSelectedMember({
                  ...selectedMember,
                  phone: e.target.value
                })}
                placeholder="+1 (555) 123-4567"
              />
              {selectedMember && renderRoleSelection(selectedMember.roles, true)}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsEditingMember(false)}>Cancel</Button>
              <Button
                onClick={handleUpdateMember}
                variant="contained"
                disabled={!selectedMember?.name || !selectedMember?.email || !selectedMember?.phone || !selectedMember?.roles?.length}
              >
                Update
              </Button>
            </DialogActions>
          </Dialog>
        </ContentContainer>
      </MainContent>
    </PageLayout>
  );
};

export default TeamSettingsPage;
